import { type Language, formatNumber } from '@eformulieren/engine'
import type { Text } from '@eformulieren/model'

/**
 * De woorden van de renderer zelf.
 *
 * Alles wat uit de definitie komt is een paar {nl, en} en is werk van de inrichter
 * (R-143). De woorden hier zijn van de schil: "verplicht", het label van de
 * inhoudsopgave, de aanhef boven een lijst meldingen. Die staan niet in de
 * definitie en horen er ook niet in; ze staan hier één keer en in beide talen,
 * zodat een taalwissel ze meeneemt (R-144).
 */
export const WORDS = {
  verplicht: { nl: 'verplicht', en: 'required' },
  fouten: { nl: 'Wat er nog niet klopt', en: 'What is not right yet' },
  waarschuwingen: { nl: 'Ter overweging', en: 'To consider' },
  secties: { nl: 'Secties', en: 'Sections' },
  nogGeenKeuze: { nl: 'nog geen keuze', en: 'no choice yet' },
  bezig: { nl: 'Bezig met verwerken', en: 'Processing' },
  // De stempel op het akkoordveld (R-19, R-89): "Akkoord gegeven door … op …".
  // Nooit een persoonsaanduiding: het product kent geen naam (R-155e), en een sleutel is geen naam.
  akkoordDoorU: { nl: 'Akkoord gegeven door u', en: 'Consent given by you' },
  akkoordDoorAnder: { nl: 'Akkoord gegeven door een andere invuller', en: 'Consent given by another form filler' },
  akkoordOp: { nl: 'op', en: 'on' },
  // Het akkoord per persoon (eisenboek 12.56).
  akkoordIkGa: { nl: 'Ik ga akkoord', en: 'I agree' },
  akkoordU: { nl: 'U', en: 'You' },
  akkoordNamens: { nl: 'namens de aanvrager', en: 'on behalf of the applicant' },
  akkoordGegevenOp: { nl: 'gaf akkoord op', en: 'gave consent on' },
  akkoordVervallen: { nl: 'vervallen na een wijziging', en: 'lapsed after a change' },
  akkoordLijst: { nl: 'Wie akkoord gaf', en: 'Who gave consent' },
  akkoordNogNiemand: { nl: 'Nog niemand gaf akkoord.', en: 'Nobody has given consent yet.' },
  akkoordUwGeldig: { nl: 'Uw akkoord geldt.', en: 'Your consent stands.' },
  akkoordUwVervallen: { nl: 'Uw akkoord is vervallen: de inhoud is gewijzigd na uw akkoord.', en: 'Your consent has lapsed: the content changed after you gave it.' },
  akkoordBezig: { nl: 'Bezig met verwerken…', en: 'Processing…' },
  akkoordMislukt: { nl: 'Dat lukte niet; probeer het opnieuw.', en: 'That did not work; please try again.' },
  // Voor wie het akkoord niet zelf geeft (besluit van 3 november 2026, R-69): de indiener verklaart.
  akkoordDoorIndiener: { nl: 'Dit akkoord geeft de indiener', en: 'This consent is given by the submitter' },
  // De tekst naast het vinkje van een ja/nee-veld en van een akkoord (`Toggle`).
  ja: { nl: 'Ja', en: 'Yes' },
  nee: { nl: 'Nee', en: 'No' },
  akkoordVink: { nl: 'Akkoord', en: 'I agree' },
  // Een leeg antwoord dat de lezer niet kan bewerken (`FieldShell`, R-133).
  nietIngevuld: { nl: 'Niet ingevuld', en: 'Not filled in' },
} as const satisfies Record<string, Text>

export type Word = keyof typeof WORDS

export function word(key: Word, language: Language): string {
  return WORDS[key][language]
}

/**
 * De tekst van de teller (R-12): hoeveel er is gebruikt en hoeveel er nog over is.
 *
 * Beide staan erin, want dat vraagt R-12 letterlijk, en boven de grens slaat "nog
 * over" om naar "te veel". De getallen gaan door `getal` van de engine, zodat de
 * duizendscheiding klopt met de taal en met wat de rest van het formulier laat
 * zien.
 */
export function counterText(
  kind: 'tekens' | 'woorden',
  used: number,
  boundary: number,
  language: Language,
): string {
  const unit = kind === 'tekens' ? { nl: 'tekens', en: 'characters' } : { nl: 'woorden', en: 'words' }
  const g = (n: number) => formatNumber(n, language)
  const excess = used - boundary
  if (excess > 0) {
    return language === 'nl'
      ? `${g(used)} van ${g(boundary)} ${unit.nl}, ${g(excess)} te veel`
      : `${g(used)} of ${g(boundary)} ${unit.en}, ${g(excess)} too many`
  }
  return language === 'nl'
    ? `${g(used)} van ${g(boundary)} ${unit.nl}, ${g(boundary - used)} over`
    : `${g(used)} of ${g(boundary)} ${unit.en}, ${g(boundary - used)} left`
}

/** De ondergrens is geen teller maar een drempel: hoeveel er nog bij moet. */
export function minimumText(used: number, boundary: number, language: Language): string {
  const g = (n: number) => formatNumber(n, language)
  if (used >= boundary) {
    return language === 'nl' ? `minimaal ${g(boundary)} woorden gehaald` : `at least ${g(boundary)} words reached`
  }
  return language === 'nl'
    ? `${g(used)} van minimaal ${g(boundary)} woorden`
    : `${g(used)} of at least ${g(boundary)} words`
}
